import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { ProjectFormModel } from 'src/app/core/models/project/project-form.model';
import { EditableProjectComponent } from '../editable-project.component';
import { EmployeeService } from 'src/app/core/services/employee.service';
import { Project } from 'src/app/core/models/project/project.interface';
import { first, of, shareReplay, tap } from 'rxjs';
import { CustomFile } from 'src/app/core/models/customFile.interface';
import { ObservableUtil } from 'src/app/core/utils/observable-util';
import { OrganizationService } from 'src/app/core/services/organization.service';
import { MinimalOrganization } from 'src/app/core/models/organization/minimal-organization.interface';
import { ProjectEditResult } from 'src/app/core/models/project/project-edit-result.model';

@Component({
  selector: 'app-edit-project',
  templateUrl: './edit-project.component.html',
  styleUrls: ['./edit-project.component.scss'],
  providers: [ProjectFormModel],
  standalone: false,
})
export class EditProjectComponent
  extends EditableProjectComponent
  implements OnInit
{
  private employeeService = inject(EmployeeService);
  private organizationService = inject(OrganizationService);

  @Input({ required: true })
  public companyId = '';

  @Input({ required: true })
  public project!: Project;
  @Output() public cancelled = new EventEmitter<void>();
  @Output() public saved = new EventEmitter<ProjectEditResult>();

  public selectedOrganization: MinimalOrganization | null = null;

  public ngOnInit(): void {
    this.projectImagePath = `companies/${this.companyId}/projects/${this.project.id}`;

    this.projectFormModel.form.patchValue(this.project);

    this.projectFormModel.form.patchValue({
      organizationId: this.project!.organization?.id,
    });

    this.projectEmployees$.next(
      this.project.employeeProfiles.concat(this.project.hiddenEmployees),
    );

    this.employeeService.setEmployeesUrl(this.companyId);

    this.employeeService
      .getCompanyEmployeesLookup()
      .pipe(
        first(),
        tap((employees) => {
          const selectedEmployeeIds = new Set(
            this.projectEmployees$.getValue().map((employee) => employee.id),
          );

          this.companyEmployees$.next(
            employees.filter(
              (employee) => !selectedEmployeeIds.has(employee.id),
            ),
          );
        }),
      )
      .subscribe();

    this.organizationService.setOrganizationsUrl(this.companyId);

    this.organizationService
      .getMinimalOrganizations()
      .pipe(
        first(),
        tap((organizations) => {
          this.companyOrganizations$.next(organizations);
          this.selectedOrganization =
            organizations.find(
              (organization) =>
                organization.id === this.projectFormModel.form.get('organizationId')?.value,
            ) ?? null;
        }),
      )
      .subscribe();

    this.projectFormModel.form
      .get('organizationId')
      ?.valueChanges.pipe(
        tap((orgId) => {
          this.selectedOrganization = this.companyOrganizations$.getValue()
            .find((org) => org.id === orgId) || null;
        }),
      )
      .subscribe();
  }

  public submitForm(): void {
    if (this.projectFormModel.form.invalid) {
      this.projectFormModel.form.markAllAsTouched();
      return;
    }

    this.saved.emit({
      formData: this.projectFormModel.form.getRawValue(),
      employeeProfiles: this.projectEmployees$.getValue(),
      projectImage: this.projectImage,
      organization: this.selectedOrganization,
    });
  }

  public onCancel(): void {
    this.cancelled.emit();
  }

  public onUploadFile(event: CustomFile): void {
    this.projectImage = event.file;

    this.previewUrl$ = ObservableUtil.getFilePreview$(this.projectImage).pipe(
      shareReplay(1),
    );
  }

  public onDeleteFile(): void {
    this.projectImage = undefined;
    this.previewUrl$ = of();
  }
}
